import React from 'react';
import { callBrowser } from "../nativebridge/JsToNative";
import { COMMON_UTIL } from "../common/util/common.util";

const PolicyManager = ({ 
  language = 'ko', 
  version, 
  onVersionChange, 
  policyVersions, 
  policyName 
}) => {
  const [currentVersion, setCurrentVersion] = React.useState(version || policyVersions[language]?.current);
  const [PolicyComponent, setPolicyComponent] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const domain = window.location.protocol + '//' + window.location.host;

  React.useEffect(() => {
    const loadPolicy = async () => {
      try {
        setLoading(true);
        
        const versionConfig = policyVersions[language];
        
        if (!versionConfig) {
          console.error(`Language ${language} not found in ${policyName}_POLICY_VERSIONS`);
          return;
        }
        
        if (!versionConfig.versions[currentVersion]) {
          console.error(`Policy version ${currentVersion} not found for language ${language}`);
          return;
        }

        const module = await versionConfig.versions[currentVersion]();
        
        if (!module.default) {
          console.error('Module does not have default export');
          return;
        }
        
        setPolicyComponent(() => module.default);
      } catch (error) {
        console.error('Failed to load policy:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPolicy();
  }, [language, currentVersion, policyVersions, policyName]);

  const handleVersionChange = (newVersion) => {
    setCurrentVersion(newVersion);
    if (onVersionChange) {
      onVersionChange(newVersion);
    }
  };

  const handleClick = (url) => {
    if (COMMON_UTIL.isWebView()) {
      callBrowser(url);
    } else {
      window.open(url);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!PolicyComponent) {
    return <div>Policy not found</div>;
  }

  return (
    <div style={{ width: '100%'}}>
      {/* 버전 선택 UI */}
      <VersionSelector 
        language={language}
        currentVersion={currentVersion}
        versions={policyVersions[language]?.versions}
        onVersionChange={handleVersionChange}
        domain={domain}
        onExternalClick={handleClick}
      />
      
      {/* 실제 약관 컴포넌트 */}
      <PolicyComponent />
    </div>
  );
};

// 버전 선택 컴포넌트
const VersionSelector = ({ language, currentVersion, versions, onVersionChange, domain, onExternalClick }) => {
  const [showVersions, setShowVersions] = React.useState(false);

  if (!versions) return null;

  const versionList = Object.keys(versions).sort().reverse();

  // 언어별 텍스트
  const texts = {
    ko: {
      version: '버전',
      close: '닫기',
      selectVersion: '버전 선택',
      otherVersions: '다른 버전 보기:',
      current: '(현재)'
    },
    en: {
      version: 'Version',
      close: 'Close',
      selectVersion: 'Select Version',
      otherVersions: 'View other versions:',
      current: '(Current)'
    }
  };

  const text = texts[language] || texts.ko;

  return (
    <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <p style={{ color: '#666' }}>
        {text.version}: {currentVersion}
        </p>
        <div>
          <button 
            onClick={() => setShowVersions(!showVersions)}
            style={{
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            {showVersions ? text.close : text.selectVersion}
          </button>
        </div>
      </div>

      {showVersions && (
        <div style={{ marginTop: '12px' }}>
          <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
            {text.otherVersions}
          </div>
          {versionList.map(version => (
            <button
              key={version}
              onClick={() => {
                onVersionChange(version);
                setShowVersions(false);
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '8px 12px',
                marginBottom: '4px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: currentVersion === version ? '#e3f2fd' : 'white',
                cursor: 'pointer',
                textAlign: 'left'
              }}
            >
              {version} {currentVersion === version ? text.current : ''}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PolicyManager; 